import React, { useEffect, useMemo, useState } from 'react'

import { useAuthentication } from '../../hooks/useAuthentication'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { GET_MY_VOUCHERS, DELETE_VOUCHERS } from './gql'
import { DataTable } from '../table/DataTable'
import { getVouchersColumns } from '../table/columns/vouchersColumns'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import Loader from '../Loader'
import PageHeader from '../Common/PageHeader'
import VoucherExpandedRow from '../table/VoucherExpandedRow'
import { toast } from 'react-toastify'
import VhsDialog from '../Common/VhsDialog'
import { MyVoucherRow } from '../../types'
import { getMyLatestActionTime, toDate } from '../../utils/utils'
import { useHistory, useLocation } from 'react-router-dom'
import { Tag } from '../../types'
import TagFilter from '../Tags/TagFilter'
import { tagIdFromSearch } from '../Tags/tagLinks'
import { GET_TAGS } from '../../api/tag'

type ChipKey = 'all' | 'created' | 'processed'

const priorityForCreated = (row: MyVoucherRow): number => {
  if (row.state === 'REJECTED') return 0
  if (row.state === 'DRAFT') return 1
  return 2
}

const emptyStateCopy: Record<ChipKey, { title: string; body: string }> = {
  all: {
    title: 'No vouchers yet',
    body: "Vouchers you create, or act on, will show up here.",
  },
  created: {
    title: "You haven't created any vouchers yet",
    body: 'Vouchers you create — including drafts — will show up here.',
  },
  processed: {
    title: "You haven't processed any vouchers yet",
    body: 'Vouchers you approve, reject, or mark as paid will show up here.',
  },
}

const Vouchers = () => {
  const { authState } = useAuthentication()
  const userId = authState.authUser.id
  const vouchers = useQuery(GET_MY_VOUCHERS, {
    context: { headers: { authorization: authState.authToken } },
  })
  const [deleteVoucher] = useMutation(DELETE_VOUCHERS)
  const allTags = useQuery(GET_TAGS, {
    context: { headers: { authorization: authState.authToken } },
    fetchPolicy: 'cache-and-network',
  })
  const [voucherToDelete, setVoucherToDelete] = useState<string | null>(null)
  const [chip, setChip] = useState<ChipKey>('all')
  const location = useLocation()
  const history = useHistory()
  // A tag chip link (?tag=<id>) preselects that tag; the filter is applied
  // to this list client-side, like the chips above.
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(() => {
    const tagId = tagIdFromSearch(location.search)
    return tagId !== null ? [tagId] : []
  })
  useEffect(() => {
    const tagId = tagIdFromSearch(location.search)
    if (tagId !== null) {
      setSelectedTagIds([tagId])
      setChip('all')
      history.replace('/panel/vouchers')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  const handleRequestDelete = (id: string) => {
    setVoucherToDelete(id)
  }

  const removeVoucher = async (id: string) => {
    try {
      const { data } = await deleteVoucher({ variables: { id } })
      if (data) {
        toast.success(`Voucher ${data.deleteVoucher} deleted successfully`)
        vouchers.refetch()
      }
    } catch (err: any) {
      console.error('Error updating voucher:', err.message)
      toast.error(err.message || `Voucher ${id} delete failed`)
    }
  }

  useEffect(() => {
    vouchers.refetch()
  }, [])

  const rows: MyVoucherRow[] = useMemo(
    () =>
      (vouchers.data?.myVouchers ?? []).map((entry: any) => ({
        ...entry.voucher,
        is_created_by_me: entry.is_created_by_me,
        my_action: entry.my_action,
      })),
    [vouchers.data],
  )

  const visibleRows = useMemo(() => {
    let filtered = rows
    if (selectedTagIds.length) {
      filtered = filtered.filter((row) =>
        row.tags?.some((t) => selectedTagIds.includes(t.id)),
      )
    }
    if (chip === 'created') {
      filtered = filtered.filter((row) => row.is_created_by_me)
    } else if (chip === 'processed') {
      filtered = filtered.filter((row) => row.my_action !== null)
    }

    const sorted = [...filtered]
    if (chip === 'created') {
      sorted.sort((a, b) => {
        const priorityDiff = priorityForCreated(a) - priorityForCreated(b)
        if (priorityDiff !== 0) return priorityDiff
        return (
          (toDate(b.created_at)?.getTime() ?? 0) -
          (toDate(a.created_at)?.getTime() ?? 0)
        )
      })
    } else if (chip === 'processed') {
      sorted.sort(
        (a, b) =>
          getMyLatestActionTime(b, userId) - getMyLatestActionTime(a, userId),
      )
    } else {
      sorted.sort(
        (a, b) =>
          (toDate(b.created_at)?.getTime() ?? 0) -
          (toDate(a.created_at)?.getTime() ?? 0),
      )
    }

    return sorted
  }, [rows, chip, userId, selectedTagIds])

  // Drop a selected tag once it's known to be deleted (e.g. a stale link).
  useEffect(() => {
    const existing = allTags.data?.tags
    if (!existing) return
    const kept = selectedTagIds.filter((id) =>
      existing.some((t: Tag) => t.id === id),
    )
    if (kept.length !== selectedTagIds.length) setSelectedTagIds(kept)
  }, [allTags.data, selectedTagIds])

  // Tags on this user's own vouchers, plus any selected tag (e.g. from a
  // link) that none of them carry, so the active filter is always visible.
  const tagOptions: Tag[] = useMemo(() => {
    const byId = new Map<number, Tag>()
    rows.forEach((row) => row.tags?.forEach((t) => byId.set(t.id, t)))
    ;(allTags.data?.tags ?? []).forEach((t: Tag) => {
      if (selectedTagIds.includes(t.id)) byId.set(t.id, t)
    })
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [rows, allTags.data, selectedTagIds])

  const columns = React.useMemo(
    () =>
      getVouchersColumns(handleRequestDelete, (tag: Tag) =>
        setSelectedTagIds([tag.id]),
      ),
    [],
  )

  if (vouchers.loading && !vouchers.data) return <Loader />
  if (vouchers.error) return <p>Error: {vouchers.error.message}</p>

  const copy = selectedTagIds.length
    ? {
        title: 'No vouchers with the selected tags',
        body: 'This list only shows vouchers you created or acted on.',
      }
    : emptyStateCopy[chip]

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader title="Vouchers" icon={DescriptionOutlinedIcon} />

      <ToggleButtonGroup
        exclusive
        value={chip}
        onChange={(_, value) => value && setChip(value)}
        size="small"
        sx={{
          mb: 3,
          p: 0.375,
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 100,
          '& .MuiToggleButton-root': {
            border: 'none',
            borderRadius: 100,
            textTransform: 'none',
            fontWeight: 500,
            color: 'text.secondary',
            px: 2,
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' },
            },
          },
        }}
      >
        <ToggleButton value="all">All</ToggleButton>
        <ToggleButton value="created">Created by me</ToggleButton>
        <ToggleButton value="processed">Processed by me</ToggleButton>
      </ToggleButtonGroup>

      {(tagOptions.length > 0 || selectedTagIds.length > 0) && (
        <Box sx={{ mb: 3, maxWidth: 420 }}>
          <TagFilter
            options={tagOptions}
            selectedIds={selectedTagIds}
            onChange={setSelectedTagIds}
          />
        </Box>
      )}

      {visibleRows.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            py: 8,
            color: 'text.secondary',
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 40, opacity: 0.5 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {copy.title}
          </Typography>
          <Typography variant="body2">{copy.body}</Typography>
        </Box>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <DataTable
            data={visibleRows}
            columns={columns}
            renderSubComponent={(row) => (
              <VoucherExpandedRow voucher={row.original} />
            )}
          />
        </div>
      )}

      <VhsDialog
        open={!!voucherToDelete}
        title="Delete Voucher"
        warningMessage="Are you sure you want to delete the voucher?"
        onClose={() => setVoucherToDelete(null)}
        onConfirm={() => {
          if (voucherToDelete) {
            removeVoucher(voucherToDelete)
          }
          setVoucherToDelete(null)
        }}
        showReasonField={false}
        color="error"
        confirmButtonText="Delete"
      />
    </Box>
  )
}

export default Vouchers
